import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { getCompanyId } from '@modules/search/components/ResultsSection/util/ResultProfile.util'
import { GroupedResultProfile, SearchProfilesResponse } from '@modules/search/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import contactsApi from '../api'
import { CompanyContactsMap, MeetingStatus } from '../types'

const useContactWithProfilesCounts = ({
  profiles = [],
  companyIds: rawCompanyIds = [],
}: {
  profiles?: SearchProfilesResponse
  companyIds?: number[]
}) => {
  const { user } = useAuthContext()
  const queryClient = useQueryClient()
  const companyIdsFromProfiles = profiles.map(i => Number(getCompanyId((i as GroupedResultProfile)?.company || i)))
  const companyIds = [...Array.from(new Set(companyIdsFromProfiles)), ...rawCompanyIds.map(id => Number(id))].filter(
    id => Boolean(id) && user?.companyId !== id,
  )

  const { data: companyContactsCounts } = useQuery({
    queryKey: [contactsApi.getContactsOverview.key, companyIds.join()],
    queryFn: () => contactsApi.getContactsOverview.fn({ companyIds }),
    enabled: !!companyIds.length,
    initialData: (): CompanyContactsMap | undefined => {
      const existingDataByCompanies = queryClient.getQueriesData<CompanyContactsMap>({
        queryKey: [contactsApi.getContactsOverview.key],
      })

      if (!existingDataByCompanies) {
        return undefined
      }

      const mergedAllRequestsData = existingDataByCompanies.reduce<CompanyContactsMap>((acc, [_, data = {}]) => {
        if (!data || Array.isArray(data)) {
          return acc
        }

        return { ...acc, ...data }
      }, {})

      const isStaleDataExist = companyIds.every(id => mergedAllRequestsData[id])

      if (isStaleDataExist) {
        return mergedAllRequestsData
      }

      return undefined
    },
    staleTime: 60 * 20 * 1000,
  })

  /** In case there is counts only for upcoming meetings, we want to show attendees view, so just skipping such counts */
  return Object.entries(companyContactsCounts || {}).reduce((acc, [companyId, data]) => {
    const withoutPast = data?.counts?.[MeetingStatus.UPCOMING] && Object.keys(data?.counts).length === 1

    acc[companyId] = { ...data, counts: withoutPast ? null : data?.counts }

    return acc
  }, {} as CompanyContactsMap)
}

export default useContactWithProfilesCounts
